import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { sendRemoteControl } from '@apis/perMachine';
import { perMachineSummarySalesData, perMachineTotalSalesData } from '@dataTypes/perMachineTypes';
import { useSetRecoilState } from 'recoil';
import errorOrYesModalState from '../../recoil/errorOrYesModal/atom';
import { imgTypes } from '@dataTypes/imgTypes';
import { SHARED_CATID_C2_WARNING_MESSAGE } from './sharedCatIdMessages';

/** 단말기 재고/재고관리·FW 자동업데이트 등 서버 기록 (원격관리 초기값·표시용) */
export interface TerminalStockInfo {
  stock?: number | null;
  stockManagementOn?: boolean | null;
  /** C2(03)으로 서버에 저장된 값: 01=사용, 00=사용안함, null=아직 기록 없음 */
  fwAutoUpdate?: string | null;
  /** C2(06)으로 서버에 저장된 값: 01=On, 00=Off, null=아직 기록 없음 */
  internetNetwork?: string | null;
  /** C2(10) 서버 기록 밝기 1~10 */
  displayBrightness?: number | null;
}

export type RemoteControlMachine = perMachineTotalSalesData | perMachineSummarySalesData;

interface RemoteControlModalProps {
  show: boolean;
  onClose: () => void;
  /** 대상 단말 (1대 이상). 1대일 때만 terminalInfo로 초기값 표시 */
  machines: RemoteControlMachine[];
  businessNum: string | undefined;
  /** 단말기 재고·재고관리 상태 (02 선택 시 초기값 및 현재 설정 표시) — 단일 선택일 때만 전달 */
  terminalInfo?: TerminalStockInfo;
  /** 대상 중 공유 catId(세션 2+)가 있으면 전송 전 경고 */
  hasSharedCatId?: boolean;
  onSuccess?: () => void;
}

type ControlType = '00' | '01' | '02' | '03' | '04' | '05' | '06' | '10';

function fwAutoUpdateRecordLabel(v: string | null | undefined): string {
  if (v === '01') return '사용';
  if (v === '00') return '사용 안 함';
  return '-';
}

function internetNetworkRecordLabel(v: string | null | undefined): string {
  if (v === '01') return 'On';
  if (v === '00') return 'Off';
  return '-';
}

function machineLabel(m: RemoteControlMachine): string {
  return (m.deviceAlias?.trim() || m.catId?.trim() || '').trim() || m.catId;
}

const RemoteControlModal = ({
  show,
  onClose,
  machines,
  businessNum,
  terminalInfo,
  hasSharedCatId = false,
  onSuccess,
}: RemoteControlModalProps) => {
  const setErrorState = useSetRecoilState(errorOrYesModalState);
  const [control, setControl] = useState<ControlType>('00');
  const [dataUsage, setDataUsage] = useState<string>('00');
  const [dataStock, setDataStock] = useState<string>('');
  const [stockOff, setStockOff] = useState(false);
  /** C2 Control 03: FW 자동업데이트 — "00" 사용안함, "01" 사용 */
  const [dataFwAutoUpdate, setDataFwAutoUpdate] = useState<string>('01');
  /** C2 Control 06: 인터넷 망 기능 — "00" Off, "01" On */
  const [dataInternetNetwork, setDataInternetNetwork] = useState<string>('01');
  /** C2 Control 10: 밝기 1~10 */
  const [dataBrightness, setDataBrightness] = useState<number>(5);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const primary = machines[0];
  const isBulk = machines.length > 1;

  useEffect(() => {
    if (!show) {
      setControl('00');
      setDataUsage('00');
      setDataStock('');
      setStockOff(false);
      setDataFwAutoUpdate('01');
      setDataInternetNetwork('01');
      setDataBrightness(5);
      setIsLoading(false);
      setProgress(null);
    } else if (machines.length === 1) {
      const off = terminalInfo?.stockManagementOn === false;
      const num = terminalInfo?.stock;
      setStockOff(off);
      setDataStock(!off && num != null && num >= 1 && num <= 99 ? String(num) : '');
      const fa = terminalInfo?.fwAutoUpdate;
      if (fa === '00' || fa === '01') {
        setDataFwAutoUpdate(fa);
      } else {
        setDataFwAutoUpdate('01');
      }
      const inet = terminalInfo?.internetNetwork;
      if (inet === '00' || inet === '01') {
        setDataInternetNetwork(inet);
      } else {
        setDataInternetNetwork('01');
      }
      const br = terminalInfo?.displayBrightness;
      if (br != null && br >= 1 && br <= 10) {
        setDataBrightness(br);
      } else {
        setDataBrightness(5);
      }
    } else {
      setStockOff(false);
      setDataStock('');
      setDataFwAutoUpdate('01');
      setDataInternetNetwork('01');
      setDataBrightness(5);
    }
  }, [show, terminalInfo, machines.length]);

  const getDataParam = (): string | undefined => {
    if (control === '00' || control === '04' || control === '05') return undefined;
    if (control === '01') return dataUsage;
    if (control === '02') return stockOff ? '-' : dataStock ? String(Number(dataStock)) : undefined;
    if (control === '03') return dataFwAutoUpdate;
    if (control === '06') return dataInternetNetwork;
    if (control === '10') return String(dataBrightness);
    return undefined;
  };

  const handleSubmit = async () => {
    if (!machines.length || !businessNum) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '오류가 발생했습니다.',
        text: '대상 단말 또는 사업자 번호 정보가 없습니다.',
        modalBG: true,
      });
      return;
    }
    if (control === '04') {
      const msg =
        machines.length > 1
          ? `선택한 ${machines.length}대 단말기에 펌웨어 업데이트를 시작합니다. 진행하시겠습니까?`
          : '단말기 펌웨어 업데이트를 시작합니다. 진행하시겠습니까?';
      const ok = window.confirm(msg);
      if (!ok) return;
    }
    if (hasSharedCatId) {
      const ok = window.confirm(SHARED_CATID_C2_WARNING_MESSAGE);
      if (!ok) return;
    }
    if (control === '02') {
      if (!stockOff && (dataStock === '' || Number(dataStock) < 1 || Number(dataStock) > 99)) {
        setErrorState({
          status: true,
          imgType: imgTypes.ERROR,
          title: '입력 오류',
          text: '재고 수(1~99)를 입력하거나 재고관리 Off를 선택하세요.',
          modalBG: true,
        });
        return;
      }
    }
    if (control === '03' && dataFwAutoUpdate !== '00' && dataFwAutoUpdate !== '01') {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '입력 오류',
        text: 'FW 자동업데이트 설정 값이 올바르지 않습니다.',
        modalBG: true,
      });
      return;
    }
    if (control === '06' && dataInternetNetwork !== '00' && dataInternetNetwork !== '01') {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '입력 오류',
        text: '인터넷 망 기능 설정 값이 올바르지 않습니다.',
        modalBG: true,
      });
      return;
    }
    if (control === '10' && (dataBrightness < 1 || dataBrightness > 10)) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '입력 오류',
        text: '밝기는 1~10 사이로 선택하세요.',
        modalBG: true,
      });
      return;
    }

    const dataParam = getDataParam() ?? undefined;
    setIsLoading(true);
    setProgress({ done: 0, total: machines.length });
    const failures: string[] = [];

    try {
      for (let i = 0; i < machines.length; i++) {
        const m = machines[i];
        const catId = m.catId?.trim() ?? '';
        try {
          const res = await sendRemoteControl(catId, businessNum, control, dataParam);
          if (!res.success) {
            failures.push(
              `${machineLabel(m)}: ${res.message ?? '단말기 세션을 찾을 수 없습니다.'}`,
            );
          }
        } catch (error: unknown) {
          const ax = error as { response?: { data?: { message?: string } }; message?: string };
          const msg =
            ax.response?.data?.message ??
            (typeof ax.message === 'string' && ax.message ? ax.message : null) ??
            '원격제어 요청에 실패했습니다.';
          failures.push(`${machineLabel(m)}: ${msg}`);
        }
        const nextProgress = { done: i + 1, total: machines.length };
        if (machines.length > 1) {
          flushSync(() => setProgress(nextProgress));
        } else {
          setProgress(nextProgress);
        }
      }
    } finally {
      setIsLoading(false);
      setProgress(null);
    }

    const okCount = machines.length - failures.length;
    if (okCount > 0) {
      onSuccess?.();
    }

    if (failures.length === machines.length) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '원격제어 실패',
        text:
          failures.slice(0, 12).join('\n') +
          (failures.length > 12 ? `\n… 외 ${failures.length - 12}건` : ''),
        modalBG: true,
      });
      return;
    }

    if (failures.length > 0) {
      setErrorState({
        status: true,
        imgType: imgTypes.ERROR,
        title: '일부 단말 원격제어 실패',
        text: `${failures.length}대 실패, ${okCount}대 성공.\n${failures.slice(0, 10).join('\n')}${failures.length > 10 ? `\n… 외 ${failures.length - 10}건` : ''}`,
        modalBG: true,
      });
    }

    if (failures.length === 0) {
      onClose();
    }
  };

  if (!show || !primary) {
    return null;
  }

  const targetSummary =
    machines.length === 1 ? (
      <strong>{machineLabel(primary)}</strong>
    ) : (
      <>
        <strong>{machines.length}대</strong>
        <span className="text-muted">
          {' '}
          ({machines.slice(0, 3).map(machineLabel).join(', ')}
          {machines.length > 3 ? ` 외 ${machines.length - 3}대` : ''})
        </span>
      </>
    );

  return (
    <Modal
      show={show}
      onHide={isLoading ? () => undefined : onClose}
      centered
      backdrop={isLoading ? 'static' : true}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>원격 관리{isBulk ? ` (일괄 ${machines.length}대)` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">단말기: {targetSummary}</p>
        {isBulk && control === '02' && (
          <p className="small text-warning mb-2">
            여러 대에 동일한 재고 값이 적용됩니다. 단말별 재고가 다르면 한 대씩 설정하세요.
          </p>
        )}
        {isBulk && control === '10' && (
          <p className="small text-warning mb-2">
            여러 대에 동일한 밝기 값이 적용됩니다. 단말별로 다르면 한 대씩 설정하세요.
          </p>
        )}
        <Form.Group className="mb-3">
          <Form.Label>제어 유형</Form.Label>
          <Form.Select value={control} onChange={(e) => setControl(e.target.value as ControlType)}>
            <option value="00">재부팅</option>
            <option value="01">단말기 사용설정</option>
            <option value="02">재고설정</option>
            <option value="03">자동 업데이트 설정</option>
            <option value="04">단말기 업데이트</option>
            <option value="05">TMS 로그 전송(요청)</option>
            <option value="06">인터넷 망 기능 설정</option>
            <option value="10">디스플레이 밝기</option>
          </Form.Select>
        </Form.Group>
        {control === '01' && (
          <Form.Group className="mb-3">
            <Form.Label>사용설정</Form.Label>
            <Form.Select value={dataUsage} onChange={(e) => setDataUsage(e.target.value)}>
              <option value="00">사용가능</option>
              <option value="01">사용불가(점검중)</option>
              <option value="02">현금만 사용가능</option>
            </Form.Select>
          </Form.Group>
        )}
        {control === '03' && (
          <Form.Group className="mb-3">
            <Form.Label>자동 업데이트</Form.Label>
            {!isBulk && (
              <p className="small text-muted mb-2">
                현재 설정(서버 기록):{' '}
                <strong>{fwAutoUpdateRecordLabel(terminalInfo?.fwAutoUpdate)}</strong>
              </p>
            )}
            <Form.Select
              value={dataFwAutoUpdate}
              onChange={(e) => setDataFwAutoUpdate(e.target.value)}
            >
              <option value="01">사용</option>
              <option value="00">사용 안 함</option>
            </Form.Select>
          </Form.Group>
        )}
        {control === '06' && (
          <Form.Group className="mb-3">
            <Form.Label>인터넷 망 기능</Form.Label>
            {!isBulk && (
              <p className="small text-muted mb-2">
                현재 설정(서버 기록):{' '}
                <strong>{internetNetworkRecordLabel(terminalInfo?.internetNetwork)}</strong>
              </p>
            )}
            <Form.Select
              value={dataInternetNetwork}
              onChange={(e) => setDataInternetNetwork(e.target.value)}
            >
              <option value="01">On</option>
              <option value="00">Off</option>
            </Form.Select>
          </Form.Group>
        )}
        {control === '04' && (
          <p className="text-muted small mb-0">업데이트 동안 단말기의 연결이 끊길 수 있습니다.</p>
        )}
        {control === '05' && (
          <p className="text-muted small mb-0">
            단말기로 control <strong>05</strong>만 전송합니다. 단말에서 TMS 로그 전송을 처리하며,
            서버에는 별도로 저장하지 않습니다.
          </p>
        )}
        {control === '10' && (
          <Form.Group className="mb-3">
            <Form.Label>밝기 (1~10)</Form.Label>
            {!isBulk && (
              <p className="small text-muted mb-2">
                현재 설정(서버 기록):{' '}
                <strong>
                  {terminalInfo?.displayBrightness != null &&
                  terminalInfo.displayBrightness >= 1 &&
                  terminalInfo.displayBrightness <= 10
                    ? String(terminalInfo.displayBrightness)
                    : '-'}
                </strong>
              </p>
            )}
            <Form.Select
              value={String(dataBrightness)}
              onChange={(e) => setDataBrightness(Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {control === '02' && (
          <Form.Group className="mb-3">
            <Form.Label>재고설정</Form.Label>
            {!isBulk &&
              (terminalInfo?.stock != null || terminalInfo?.stockManagementOn != null) && (
                <p className="small text-muted mb-2">
                  현재: 재고 {terminalInfo?.stock != null ? `${terminalInfo.stock}개` : '-'},{' '}
                  재고관리{' '}
                  {terminalInfo?.stockManagementOn === true
                    ? 'On'
                    : terminalInfo?.stockManagementOn === false
                      ? 'Off'
                      : '-'}
                </p>
              )}
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Form.Control
                type="number"
                min={1}
                max={99}
                placeholder="1~99"
                value={dataStock}
                onChange={(e) => setDataStock(e.target.value)}
                disabled={stockOff}
                style={{ width: 80 }}
              />
              <span>또는</span>
              <Form.Check
                type="checkbox"
                id="remote-stock-off"
                label="재고관리 Off (-)"
                checked={stockOff}
                onChange={(e) => setStockOff(e.target.checked)}
              />
            </div>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          취소
        </Button>
        <Button variant="primary" onClick={() => void handleSubmit()} disabled={isLoading}>
          {isLoading && progress
            ? `전송 중… ${progress.done}/${progress.total}`
            : isLoading
              ? '처리 중...'
              : '전송'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoteControlModal;
